import type { View, WidgetConfig } from "./config";
import { monthGridDateRange } from "./month-grid";

export type EmbedTag = {
  id: string;
  name: string;
  group_name: string;
};

export type EmbedEvent = {
  id: string;
  /**
   * External events are third-party listings; event_link is their CTA.
   * School events are closed (the school handles sign-up): no link, no
   * app registration.
   */
  event_type: "internal" | "external" | "school";
  name: string;
  tags: EmbedTag[];
  starts_at: string;
  ends_at: string;
  timezone: string;
  address_title: string;
  address_full: string;
  description: string;
  contact_name: string;
  contact_email: string;
  image: string | null;
  event_link: string;
  recurring_rule_id: string | null;
  recurrence_frequency: string | null;
};

export type PaginatedResponse = {
  next: string | null;
  previous: string | null;
  results: EmbedEvent[];
};

export type FeedOptions = {
  view: View;
  anchorDate: Date | null;
};

const MONTHLY_PAGE_SIZE = 100;
const MONTHLY_MAX_PAGES = 5;

function maxIso(
  a: string | undefined,
  b: string | undefined,
): string | undefined {
  if (!a) return b;
  if (!b) return a;
  const da = new Date(a).getTime();
  const db = new Date(b).getTime();
  if (Number.isNaN(da)) return b;
  if (Number.isNaN(db)) return a;
  return da >= db ? a : b;
}

function minIso(
  a: string | undefined,
  b: string | undefined,
): string | undefined {
  if (!a) return b;
  if (!b) return a;
  const da = new Date(a).getTime();
  const db = new Date(b).getTime();
  if (Number.isNaN(da)) return b;
  if (Number.isNaN(db)) return a;
  return da <= db ? a : b;
}

export function buildFeedUrl(
  config: WidgetConfig,
  opts: FeedOptions = { view: "list", anchorDate: null },
): string {
  const url = new URL("/api/v1/embed/events/", config.apiBaseUrl);

  let pageSize = config.pageSize;
  let startsAfter = config.startsAfter;
  let startsBefore = config.startsBefore;

  if (opts.anchorDate) {
    if (opts.view === "agenda" || opts.view === "pinboard") {
      startsAfter = maxIso(startsAfter, opts.anchorDate.toISOString());
    } else if (opts.view === "monthly") {
      const range = monthGridDateRange(
        opts.anchorDate,
        config.monthlyWeekStart,
      );
      startsAfter = maxIso(startsAfter, range.start.toISOString());
      startsBefore = minIso(startsBefore, range.end.toISOString());
      pageSize = MONTHLY_PAGE_SIZE;
    }
  }

  url.searchParams.set("page_size", String(pageSize));

  if (startsAfter) {
    url.searchParams.set("starts_after", startsAfter);
  }

  if (startsBefore) {
    url.searchParams.set("starts_before", startsBefore);
  }

  for (const tag of config.tag) {
    url.searchParams.append("tag", tag);
  }

  return url.toString();
}

export async function fetchEvents(
  url: string,
  signal: AbortSignal,
): Promise<PaginatedResponse> {
  const response = await fetch(url, {
    headers: { Accept: "application/json" },
    signal,
  });

  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  return (await response.json()) as PaginatedResponse;
}

export async function fetchEventsAllPages(
  initialUrl: string,
  signal: AbortSignal,
  maxPages: number = MONTHLY_MAX_PAGES,
): Promise<EmbedEvent[]> {
  const all: EmbedEvent[] = [];
  let currentUrl: string | null = initialUrl;
  let page = 0;
  while (currentUrl && page < maxPages) {
    const payload: PaginatedResponse = await fetchEvents(currentUrl, signal);
    if (signal.aborted) return all;
    all.push(...(payload.results ?? []));
    currentUrl = payload.next;
    page++;
  }
  return all;
}
